import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const NewContractAnalysis = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingDocs, setFetchingDocs] = useState(true);
    const [formData, setFormData] = useState({
        document_id: '',
        analysis_type: 'FULL',
        case_id: '',
        client_id: '',
        comparison_document_id: ''
    });
    const [result, setResult] = useState(null);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/documents/', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDocuments(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            console.error('Error fetching documents:', err);
        } finally {
            setFetchingDocs(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.document_id) {
            alert('Please select a document to analyze');
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/contracts/analyses/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    document_id: parseInt(formData.document_id),
                    analysis_type: formData.analysis_type,
                    case_id: formData.case_id ? parseInt(formData.case_id) : null,
                    client_id: formData.client_id ? parseInt(formData.client_id) : null,
                    comparison_document_id: formData.comparison_document_id ? parseInt(formData.comparison_document_id) : null
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Analysis failed');
            }

            setResult(data);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getRiskColor = (category) => {
        switch (category) {
            case 'LOW': return 'bg-green-500';
            case 'MEDIUM': return 'bg-yellow-500';
            case 'HIGH': return 'bg-orange-500';
            case 'CRITICAL': return 'bg-red-500';
            default: return 'bg-gray-400';
        }
    };

    const getRiskBadgeColor = (level) => {
        switch (level) {
            case 'NONE': return 'bg-green-100 text-green-800';
            case 'LOW': return 'bg-blue-100 text-blue-800';
            case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
            case 'HIGH': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="mb-6">
                <nav className="text-sm text-gray-500 mb-2">
                    <Link to="/contracts" className="hover:text-blue-600">Contract Analyzer</Link>
                    <span className="mx-2">›</span>
                    <span>New Analysis</span>
                </nav>
                <h1 className="text-2xl font-bold text-gray-900">Analyze Contract</h1>
                <p className="text-gray-600 mt-1">Upload or select a document for AI-powered analysis</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Select Document *
                            </label>
                            {fetchingDocs ? (
                                <div className="h-10 bg-gray-100 rounded-lg animate-pulse"></div>
                            ) : (
                                <select
                                    required
                                    value={formData.document_id}
                                    onChange={(e) => setFormData({ ...formData, document_id: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">Choose a document</option>
                                    {documents.map(doc => (
                                        <option key={doc.id} value={doc.id}>
                                            {doc.title} ({doc.file_type || 'Unknown type'})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Analysis Type
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: 'FULL', label: 'Full Analysis', desc: 'Complete contract review' },
                                    { value: 'RISK_ONLY', label: 'Risk Assessment', desc: 'Focus on risks only' },
                                    { value: 'CLAUSE_ONLY', label: 'Clause Extraction', desc: 'Extract clauses' },
                                    { value: 'COMPARISON', label: 'Compare Contracts', desc: 'Compare two contracts' }
                                ].map(type => (
                                    <label
                                        key={type.value}
                                        className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${formData.analysis_type === type.value
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            name="analysis_type"
                                            value={type.value}
                                            checked={formData.analysis_type === type.value}
                                            onChange={(e) => setFormData({ ...formData, analysis_type: e.target.value })}
                                            className="sr-only"
                                        />
                                        <span className="font-medium text-gray-900">{type.label}</span>
                                        <span className="text-xs text-gray-500">{type.desc}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {formData.analysis_type === 'COMPARISON' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Compare With Document
                                </label>
                                <select
                                    value={formData.comparison_document_id}
                                    onChange={(e) => setFormData({ ...formData, comparison_document_id: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="">Select comparison document</option>
                                    {documents.filter(d => d.id.toString() !== formData.document_id).map(doc => (
                                        <option key={doc.id} value={doc.id}>{doc.title}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium">AI Analysis</p>
                                    <p>The analysis uses AI to extract key terms, identify risks, and provide recommendations. Processing may take a minute.</p>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !formData.document_id}
                            className={`w-full py-3 rounded-lg text-white font-medium flex items-center justify-center ${loading || !formData.document_id
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                                }`}
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Analyzing Contract...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                    </svg>
                                    Start Analysis
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Results */}
                <div>
                    {result ? (
                        <div className="space-y-6">
                            {/* Risk Score Card */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900">Risk Assessment</h3>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getRiskColor(result.risk_category)}`}>
                                        {result.risk_category || 'Unknown'}
                                    </span>
                                </div>

                                {result.overall_risk_score !== null && (
                                    <div className="mb-4">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-gray-500">Risk Score</span>
                                            <span className="font-medium">{result.overall_risk_score}/100</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-3">
                                            <div
                                                className={`h-3 rounded-full transition-all ${getRiskColor(result.risk_category)}`}
                                                style={{ width: `${result.overall_risk_score}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4 text-center">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-2xl font-bold text-gray-900">{result.clause_count || 0}</p>
                                        <p className="text-xs text-gray-500">Clauses Found</p>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <p className="text-2xl font-bold text-gray-900">{result.risk_count || 0}</p>
                                        <p className="text-xs text-gray-500">Risks Identified</p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            {result.summary && (
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Contract Summary</h3>
                                    <p className="text-gray-700 whitespace-pre-line">{result.summary}</p>
                                </div>
                            )}

                            {/* Extracted Clauses */}
                            {result.clauses?.length > 0 && (
                                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="p-6 border-b border-gray-100">
                                        <h3 className="font-semibold text-gray-900">Extracted Clauses</h3>
                                    </div>
                                    <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                                        {result.clauses.slice(0, 10).map((clause, index) => (
                                            <div key={index} className="p-4">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <span className="text-xs text-gray-500 uppercase">{clause.clause_type}</span>
                                                        <h4 className="font-medium text-gray-900">{clause.title || 'Untitled Clause'}</h4>
                                                    </div>
                                                    <span className={`px-2 py-1 text-xs rounded-full ${getRiskBadgeColor(clause.risk_level)}`}>
                                                        {clause.risk_level}
                                                    </span>
                                                </div>
                                                {clause.summary && (
                                                    <p className="text-sm text-gray-600 mt-2">{clause.summary}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* View Full Analysis Button */}
                            <Link
                                to={`/contracts/${result.id}`}
                                className="block w-full text-center py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                            >
                                View Full Analysis →
                            </Link>
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Analyze</h3>
                            <p className="text-gray-500">
                                Select a document and click "Start Analysis" to begin.
                                The AI will extract clauses, assess risks, and provide recommendations.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewContractAnalysis;
